import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "bastion.archivedChatSessionIds";

function parseStoredIds(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const x of parsed) {
      if (typeof x !== "string") continue;
      const id = x.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
  } catch {
    return [];
  }
}

function loadArchivedIds(): string[] {
  if (typeof window === "undefined") return [];
  return parseStoredIds(window.localStorage.getItem(STORAGE_KEY));
}

function persistArchivedIds(ids: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // quota / private mode
  }
}

export function useArchivedChats() {
  const [archivedSessionIds, setArchivedSessionIds] = useState<string[]>(loadArchivedIds);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setArchivedSessionIds(parseStoredIds(event.newValue));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setAndPersist = useCallback((ids: string[]) => {
    setArchivedSessionIds(ids);
    persistArchivedIds(ids);
  }, []);

  const isArchived = useCallback(
    (sessionId: string) => archivedSessionIds.includes(sessionId),
    [archivedSessionIds],
  );

  const archive = useCallback(
    (sessionId: string) => {
      const trimmed = sessionId.trim();
      if (!trimmed) return;
      if (archivedSessionIds.includes(trimmed)) return;
      setAndPersist([trimmed, ...archivedSessionIds]);
    },
    [archivedSessionIds, setAndPersist],
  );

  const unarchive = useCallback(
    (sessionId: string) => {
      const trimmed = sessionId.trim();
      if (!trimmed) return;
      setAndPersist(archivedSessionIds.filter((id) => id !== trimmed));
    },
    [archivedSessionIds, setAndPersist],
  );

  const archivedIdSet = useMemo(() => new Set(archivedSessionIds), [archivedSessionIds]);

  return {
    archivedSessionIds,
    archivedIdSet,
    isArchived,
    archive,
    unarchive,
  };
}
